<template>
  <div ref="modal_form" class="modal-form">
    <div class="modal-header justify-content-center pt-3">
      <label>{{ label }}</label>
    </div>
    <div class="row mr-3 ml-3 v-theme--light">
      <div class="col-10">
        <v-text-field
          :label="i18n.global.t('pages.dailyReportLayout.name')"
          v-model="form.name"
          :error-messages="errors.name"
        />
      </div>
    </div>
    <div class="row mr-3 ml-3 v-theme--light">
      <div class="col-5">
        <v-select
          class="small"
          v-model="selectedReportLayout"
          :items="DISPLAY_REPORT_LAYOUTS"
          item-title="label"
          item-value="key"
          v-bind="{'return-object': true}"
          :label="i18n.global.t('pages.dailyReportLayout.modal.label.reportLayout.title')"
          :clearable="false"
        />
      </div>
    </div>
    <div class="row mr-3 ml-3 v-theme--light">
      <div class="col-5">
        <v-select
          class="small"
          v-model="selectedPageComposition"
          :items="displayPageCompositions()"
          :label="i18n.global.t('pages.dailyReportLayout.modal.label.pageUnit')"
          :clearable="false"
        >
        </v-select>
      </div>
    </div>
    <div class="row mr-3 ml-3 v-theme--light">
      <v-checkbox
        v-model="allSelect"
        dense
        class="mb-0 all-select"
        :label="i18n.global.t('pages.dailyReportLayout.modal.label.allSelect')"
        @change="selectAll"
      />
      <v-select
        class="small"
        v-model="selectedChartDisplay"
        :items="chartDisplayList"
        :label="i18n.global.t('pages.dailyReportLayout.modal.label.chartDisplay')"
        multiple
      />
    </div>
    <div class="row mr-3 ml-3 v-theme--light">
      <div class="col-10">
        <v-textarea
          :label="i18n.global.t('pages.dailyReportLayout.modal.label.memo')"
          height="300px"
          v-model="form.note"
        ></v-textarea>
      </div>
    </div>
    <Buttons
      @cancel="closeModal"
      @submit="submit"
      :running="running"
      :hasError="hasError"
    />
  </div>
</template>

<script setup>
import 'gitart-vue-dialog/dist/style.css';
import Api from '@/services/api/ApiServiceFabrick';
import Buttons from '@/components/DailyReportLayoutParts/ModalButtons';
import {
  dailyReportChartDisplay,
  DAILY_REPORT_PAGE_UNITS,
} from '@/services/consts';
import { ref, defineProps, defineEmits, watch, onMounted } from 'vue';
import i18n from '@/lang/i18n';
import { DISPLAY_REPORT_LAYOUTS } from "@/services/consts";

const props = defineProps(['item', 'showFlash', 'modelValue']);
const emit = defineEmits(['updateItem', 'update:modelValue']);

const chartDisplayList = [];
dailyReportChartDisplay.forEach((v) => {
  chartDisplayList.push(v['label']);
});
const selectedChartDisplay = ref([]);

const form = ref({ id: '' });
const errors = ref({});
const selectedPageComposition = ref(DAILY_REPORT_PAGE_UNITS[0].label);
const name = ref(null);
const running = ref(false);
const modal_form = ref(null);
const allSelect = ref(false);
const selectedReportLayout = ref(null);

onMounted(() => {
  mirrorForm();
});

const displayPageCompositions = () => {
  return DAILY_REPORT_PAGE_UNITS.map((v) => {
    return v.label;
  });
};

const selectAll = () => {
  if (allSelect.value) {
    selectedChartDisplay.value = chartDisplayList;
  } else {
    selectedChartDisplay.value = [];
  }
};
watch(selectedChartDisplay, () => {
  if (arrayEquals(chartDisplayList, selectedChartDisplay.value)) {
    allSelect.value = true;
  } else {
    allSelect.value = false;
  }
});
const arrayEquals = (baseArr, arr) => {
  let ret = true;
  baseArr.forEach((v) => {
    if (arr.indexOf(v) < 0) ret = false;
  });
  return ret;
};

const validate = () => {
  let invalid = false;
  invalid = !validateName() || invalid;
  return !invalid;
};
const validateName = () => {
  if (form.value.name) {
    errors.value.name = [];
    return true;
  } else {
    errors.value.name = [i18n.global.t('pages.dailyReportLayout.modal.validate.input')];
    return false;
  }
};
const submit = () => {
  if (!validate()) {
    modal_form.value.scrollTop = 0;
    return;
  }
  running.value = true;

  let method = Api().createDailyReportLayout;
  if (props.item.id) {
    method = Api().updateDailyReportLayout;
  }

  const params = makeRequestParam();
  method(params)
    .then(() => {
      emit('updateItem');
      props.showFlash(i18n.global.t('pages.common.messages.success'), 'success', true);
      running.value = false;
      closeModal();
    })
    .catch((error) => {
      const res = error.response;
      switch (res?.status) {
        case 401:
          props.showFlash(i18n.global.t('pages.common.messages.failed'), 'error', false);
          props.closeModal();
          this.loginWithRedirect({
            appState: { targetUrl: location.pathname },
          });
          break;
        case 403:
          props.showFlash(i18n.global.t('pages.common.messages.failed'), 'error', false);
          location.reload();
          break;
        case 422:
          errors.value = res.data.errors;
          modal_form.value.scrollTop = 0;
      }
      running.value = false;
    });
};

const makeRequestParam = () => {
  const param = {
    id: form.value.id,
    name: form.value.name,
    layout: selectedReportLayout.value.key,
    note: form.value.note,
  };
  setSelectedChartDisplayToParam(param);
  setPageCompositionsToParam(param);
  setIsNoteToParam(param);
  if (!form.value?.id) param.id = '';
  return param;
};

const mirrorForm = () => {
  form.value = JSON.parse(JSON.stringify(props.item));
  setSelectedLayout();
  setSelectedChartDisplayFromForm();
  setPageCompositionsFromForm();
};

const setSelectedChartDisplayFromForm = () => {
  selectedChartDisplay.value = [];
  dailyReportChartDisplay.forEach((v) => {
    if (form.value[v.key]) selectedChartDisplay.value.push(v.label);
  });
};

const setSelectedChartDisplayToParam = (param) => {
  dailyReportChartDisplay.forEach((v) => {
    param[v.key] = selectedChartDisplay.value.indexOf(v.label) >= 0;
  });
};

const setPageCompositionsFromForm = () => {
  DAILY_REPORT_PAGE_UNITS.forEach((v) => {
    if (v.data == form.value.page_structure)
      selectedPageComposition.value = v.label;
  });
};

function setSelectedLayout() {
  if (!form.value.layout) {
    return selectedReportLayout.value = DISPLAY_REPORT_LAYOUTS[0];
  }

  DISPLAY_REPORT_LAYOUTS.forEach((LayoutList) => {
    if (LayoutList.key == form.value.layout) {
      selectedReportLayout.value = LayoutList;
    }
  });
}

const setPageCompositionsToParam = (param) => {
  DAILY_REPORT_PAGE_UNITS.forEach((v) => {
    if (v.label == selectedPageComposition.value) param.page_structure = v.data;
  });
};

const setIsNoteToParam = (param) => {
  param.is_note = form.value.note ?
  i18n.global.t('pages.dailyReportLayout.modal.isNote.yes') :
  i18n.global.t('pages.dailyReportLayout.modal.isNote.none');
};

const closeModal = () => {
  emit('update:modelValue', false);
};
</script>

<style scoped lang="scss">
.modal-form {
   height: auto;
   overflow: auto;
}
.modal-header {
  font-weight: 600;
}
.custom-rotate-transition {
  &-enter-from {
    transform: translate(0, 30px) rotate(20deg);
    opacity: 0;
  }

  &-leave-to {
    transform: translate(0, 30px) rotate(10deg);
    opacity: 0;
  }
}

.custom-from-bottom-transition {
  &-enter-from {
    transform: translate(0, 100%);
    opacity: 0;
  }

  &-leave-to {
    transform: translate(0, -30%);
    opacity: 0;
  }
}
</style>
